/**
 * trust-config.ts — constants for the Trust entity dialog.
 *
 * Ports the Trust-branch subset of SPFx `entityGroupConfig.ts` (4 sub-types +
 * CALL_CLASS_MAP + titling conventions) plus `idValidation` governing-law
 * states list. Per docs/port-design/category-cards/trust.md, Estate is
 * explicitly excluded from the Trust branch (separate dialog).
 *
 * Per Bill's 2026-04-23 admin-configurable architecture mandate, these MUST
 * move to SQL catalog tables in a later wave:
 *   - TrustSubTypes catalog (sub-type enum + call class + tin code + flags)
 *   - GoverningLawStates (US states + US territories)
 *
 * For v0.1.x these are hardcoded; the config hook will proxy `/v3/config/*`
 * once the catalog tables ship.
 */

import type { CustomerType } from '@retail-ops/shared-types/enums';

// -----------------------------------------------------------------------------
// Trust sub-types
// -----------------------------------------------------------------------------

/**
 * Canonical sub-type identifiers used inside the dialog + derivation hook.
 * These are NOT (yet) in the shared CustomerType enum — that enum only
 * carries the coarser 'Trust' label today. The derivation hook publishes
 * 'Trust' into person.customerType for now, and stores the finer sub-type
 * in `person.extra.trustSubType` so downstream Core Director mapping can
 * key off it. Wave-3 promotion: add these four values to the shared enum.
 */
export type TrustSubType =
  | 'RevocableTrust'
  | 'IrrevocableTrust'
  | 'QIT'
  | 'SelfSettledSNT';

export interface TrustSubTypeOption {
  readonly value: TrustSubType;
  readonly label: string;
  /** Short officer-facing descriptor (shown as help text under the picker). */
  readonly helpText: string;
  /** Core Director Call Class on the CIF record (legacy field 23). */
  readonly cifCallClass: string;
  /** Core Director Call Class on the Account record (often differs from CIF). */
  readonly acctCallClass: string;
  /** TIN code default — S (grantor SSN) or E (own EIN). Officer can override. */
  readonly tinCodeDefault: 'S' | 'E';
  /** Whether this sub-type is revocable (grantor retains control). */
  readonly isRevocable: boolean;
  /** Consumer-account flag — QIT keeps Y; others N. */
  readonly consumerFlag: boolean;
  /** Whether this sub-type uses the Branch# + '0' + AcctNum fake-CIF convention. */
  readonly usesFakeCifConvention: boolean;
  /** True when Risk Management must review the trust agreement BEFORE opening. */
  readonly requiresRiskReview: boolean;
  /** True when Manager-role approval is required before CIF commit. */
  readonly requiresManagerApproval: boolean;
  /** True when the grantor must NOT be authorized as a signer on the account. */
  readonly grantorCannotSign: boolean;
  /** True when debit cards must be blocked on accounts opened under this sub-type. */
  readonly blockDebitCards: boolean;
  /** Titling-convention suffix template used on signature cards. */
  readonly titlingSuffixTemplate: string;
}

/**
 * The 4 Trust sub-types (Estate excluded per people-form-port.md:392).
 *
 * Sourced from:
 *   - SPFx `entityGroupConfig.ts:112-133`
 *   - SPFx `models/types.ts:91-99` (CALL_CLASS_MAP)
 *   - `Docs/ENTITY-TYPE-PROCEDURE-ANALYSIS.md` §1-§4
 */
export const TRUST_SUB_TYPES: ReadonlyArray<TrustSubTypeOption> = [
  {
    value: 'RevocableTrust',
    label: 'Revocable Trust',
    helpText: 'Grantor retains control; CDD-exempt; grantor SSN used on CIF.',
    cifCallClass: '30',
    acctCallClass: '30',
    tinCodeDefault: 'S',
    isRevocable: true,
    consumerFlag: false,
    usesFakeCifConvention: true,
    requiresRiskReview: false,
    requiresManagerApproval: false,
    grantorCannotSign: false,
    blockDebitCards: false,
    titlingSuffixTemplate: 'Trust UAD {agreementDate}',
  },
  {
    value: 'IrrevocableTrust',
    label: 'Irrevocable Trust',
    helpText:
      'Non-grantor; uses own EIN. Full trust agreement must go to Risk Management BEFORE opening.',
    cifCallClass: '24',
    acctCallClass: '24',
    tinCodeDefault: 'E',
    isRevocable: false,
    consumerFlag: false,
    usesFakeCifConvention: false,
    requiresRiskReview: true,
    requiresManagerApproval: true,
    grantorCannotSign: false,
    blockDebitCards: false,
    titlingSuffixTemplate: 'Trust UAD {agreementDate}',
  },
  {
    value: 'QIT',
    label: 'Qualified Income Trust (Miller)',
    helpText:
      'Medicaid spend-down trust; grantor SSN on CIF. No debit cards. Coordinate with MO DFS.',
    cifCallClass: '9',
    acctCallClass: '31',
    tinCodeDefault: 'S',
    isRevocable: false,
    consumerFlag: true,
    usesFakeCifConvention: true,
    requiresRiskReview: false,
    requiresManagerApproval: false,
    grantorCannotSign: false,
    blockDebitCards: true,
    titlingSuffixTemplate: 'Qualified Income Trust UAD {agreementDate}',
  },
  {
    value: 'SelfSettledSNT',
    label: 'Self-Settled Special Needs Trust',
    helpText:
      'Irrevocable SNT; own EIN. Grantor is NOT authorized as a signer on the account.',
    cifCallClass: '24',
    acctCallClass: '24',
    tinCodeDefault: 'E',
    isRevocable: false,
    consumerFlag: false,
    usesFakeCifConvention: true,
    requiresRiskReview: false,
    requiresManagerApproval: false,
    grantorCannotSign: true,
    blockDebitCards: false,
    titlingSuffixTemplate: 'Self-Settled Special Needs Trust UAD {agreementDate}',
  },
];

/** Fast lookup from sub-type value → option record. */
export const TRUST_SUB_TYPE_MAP: ReadonlyMap<TrustSubType, TrustSubTypeOption> = new Map(
  TRUST_SUB_TYPES.map((t) => [t.value, t] as const),
);

/** Retrieve a trust sub-type option, or null when unknown. */
export function getTrustSubType(value: string | undefined | null): TrustSubTypeOption | null {
  if (!value) return null;
  return TRUST_SUB_TYPE_MAP.get(value as TrustSubType) ?? null;
}

// -----------------------------------------------------------------------------
// Governing-law states — 50 + DC + US territories
// -----------------------------------------------------------------------------

export interface GoverningLawStateOption {
  readonly code: string;
  readonly name: string;
}

/**
 * State list for the "Trust State (governing law)" dropdown in
 * TrustIdentitySection. Mirrors shared AddressSection's US_STATES so the
 * officer sees the same familiar 57-entry list. Defaults to the branch
 * state in the section, not here.
 */
export const GOVERNING_LAW_STATES: ReadonlyArray<GoverningLawStateOption> = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'AS', name: 'American Samoa' }, { code: 'GU', name: 'Guam' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'PR', name: 'Puerto Rico' }, { code: 'VI', name: 'US Virgin Islands' },
];

// -----------------------------------------------------------------------------
// Governing-trustee count options
// -----------------------------------------------------------------------------

/**
 * Trusts typically name 1-4 governing trustees; values above 4 are rare but
 * possible. We show 1-6 + "7+" so the officer doesn't hit an artificial cap.
 */
export const TRUSTEE_COUNT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '1', label: '1 trustee' },
  { value: '2', label: '2 trustees' },
  { value: '3', label: '3 trustees' },
  { value: '4', label: '4 trustees' },
  { value: '5', label: '5 trustees' },
  { value: '6', label: '6 trustees' },
  { value: '7+', label: '7 or more trustees' },
];

// -----------------------------------------------------------------------------
// Trust CIP document slot codes (§13c)
// -----------------------------------------------------------------------------

/**
 * Hard-required document slots for every Trust sub-type. These codes MUST
 * be present as DocumentRequirements rows in the SQL catalog (seed file
 * `scripts/seed/11-entity-rules.sql`). If the catalog doesn't return them,
 * TrustCipSection renders them anyway as locally-enforced slots so the
 * officer can't complete CIP without the executed-agreement pages.
 */
export const TRUST_HARD_DOC_CODES: ReadonlyArray<string> = [
  'TRUST_AGREEMENT',
  'TRUST_SUCCESSOR_PAGE',
  'TRUST_SIGNATURE_PAGE',
  'TRUST_CERT',
];

// -----------------------------------------------------------------------------
// Helper labels
// -----------------------------------------------------------------------------

/** Build the display-name titling suffix (e.g., "Trust UAD 12-14-2015"). */
export function buildTrustTitlingSuffix(
  subType: TrustSubType,
  agreementDateIso: string | undefined,
): string {
  const opt = TRUST_SUB_TYPE_MAP.get(subType);
  if (!opt) return '';
  const formatted = formatAgreementDateDisplay(agreementDateIso);
  return opt.titlingSuffixTemplate.replace('{agreementDate}', formatted || '[date]');
}

/** Format YYYY-MM-DD → MM-DD-YYYY for UAD display. Returns empty string on bad input. */
export function formatAgreementDateDisplay(iso: string | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return '';
  return `${m[2]}-${m[3]}-${m[1]}`;
}

/**
 * Maps Trust sub-types onto the coarse shared `CustomerType` enum value.
 * The shared enum only carries 'Trust' today; all 4 sub-types collapse to
 * that. The finer sub-type is stored in `person.extra.trustSubType` for
 * downstream mapping. Wave-3 promotion: extend CustomerType with the 4
 * fine-grained values and remove this collapse.
 */
export function trustSubTypeToCustomerType(_subType: TrustSubType | undefined | null): CustomerType {
  return 'Trust';
}

// -----------------------------------------------------------------------------
// Fake-CIF convention tag (parity with SPFx types.ts:253)
// -----------------------------------------------------------------------------

/** Identifier value written to `person.extra.cifConvention` when applicable. */
export const TRUST_BRANCH_PREFIX_CIF_CONVENTION = 'branchPrefix' as const;

// -----------------------------------------------------------------------------
// CDD exemption constants (all Trust sub-types)
// -----------------------------------------------------------------------------

/** Core Director CDD Exempt Reason Code 17 — Trust/Estate exemption. */
export const TRUST_CDD_EXEMPT_REASON = '17' as const;

/** Auto-fill occupation for Trust entities (Core Director field 313). */
export const TRUST_OCCUPATION_LABEL = 'Trust' as const;
